import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { keyframes, type Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { salesApi } from "../../client.js";
import { SalesErrorState } from "../../components/SalesEmptyState.js";
import { ChatTurn, MissionDivider, ThinkingIndicator } from "./blocks.js";
import { groupTurns, type ChatItem } from "./groupTurns.js";

const pulse = keyframes`0%,100% { opacity: 1 } 50% { opacity: 0.25 }`;

/** A turn is "live" (agent working) if its latest action is this fresh. */
const WORKING_WINDOW_MS = 45_000;

/**
 * Brain ▸ Live Activity, rendered as a Claude-style chat transcript —
 * NEWEST ON TOP. `groupTurns` folds each Plan/Observe/Reflect step into one
 * "turn" (thinking → tool use → what it saw → decision) across two speakers
 * (Master orchestrator, Discovery field-agent).
 *
 * Real-Claude-chat touches:
 *   - newest turn at the top (auto-sticks to the top unless you scroll down);
 *   - a live "thinking…" indicator while the agent is mid-tick;
 *   - newly-arrived turns stream their reasoning in (typewriter);
 *   - each turn shows "thought for Xs" from the recorded reasoning latency.
 */
export function ChatActivity(): JSX.Element {
  const feed = useQuery({
    queryKey: ["sales", "brain-feed"],
    queryFn: salesApi.getBrainFeed,
    refetchInterval: 5_000,
  });

  const items = feed.data?.items ?? [];
  const chat = useMemo(() => groupTurns(items), [items]);
  // Newest first — the operator watches the latest thought arrive at the top.
  const view = useMemo(() => [...chat].reverse(), [chat]);
  const { ref, atTop, scrollToTop } = useStickToTop(chat.length);

  const latestTs = useMemo(() => {
    let max = 0;
    for (const it of chat) if (it.type === "turn") max = Math.max(max, Date.parse(it.turn.ts));
    return max;
  }, [chat]);
  const working = latestTs > 0 && Date.now() - latestTs < WORKING_WINDOW_MS;
  const turnCount = chat.reduce((n, it) => (it.type === "turn" ? n + 1 : n), 0);

  // Turns that arrived since the last poll → stream their reasoning in.
  const animateKeys = useNewTurnKeys(view);

  return (
    <Box
      component="section"
      aria-label="Agent activity chat"
      sx={{
        display: "grid",
        gridTemplateRows: "auto 1fr",
        minHeight: 0,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
      }}
    >
      <Box
        component="header"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}
      >
        <Typography component="h2" sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          Live activity{turnCount ? ` (${turnCount})` : ""}
        </Typography>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 10.5, color: "text.disabled" }}>
          <Box
            aria-hidden
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: working ? "success.main" : "text.disabled",
              animation: working ? `${pulse} 1.4s ease-in-out infinite` : "none",
            }}
          />
          {working ? "working…" : "live · updates every 5 s"}
        </Box>
      </Box>

      <Box sx={{ position: "relative", minHeight: 0 }}>
        <Box
          ref={ref}
          sx={{
            height: { xs: "62vh", md: "calc(100dvh - 232px)" },
            minHeight: 320,
            overflowY: "auto",
            overflowX: "hidden",
            px: 2,
            py: 2,
          }}
        >
          {feed.isLoading ? (
            <Skeleton variant="rounded" sx={{ height: 240, borderRadius: 1 }} />
          ) : feed.isError ? (
            <SalesErrorState error={feed.error} surface="brain" />
          ) : turnCount === 0 ? (
            <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
              The agent hasn't recorded any activity yet — once the autonomous
              loop starts a mission, you'll watch it think, search, and decide here.
            </Typography>
          ) : (
            <Box component="ol" sx={{ display: "grid", gap: 2, listStyle: "none", m: 0, p: 0 }}>
              {working ? <ThinkingIndicator speaker="master" /> : null}
              {view.map((it) =>
                it.type === "divider" ? (
                  <Box component="li" key={it.key} sx={{ listStyle: "none" }}>
                    <MissionDivider label={it.missionLabel} />
                  </Box>
                ) : (
                  <ChatTurn key={it.key} turn={it.turn} animate={animateKeys.has(it.turn.key)} />
                ),
              )}
            </Box>
          )}
        </Box>

        {!atTop && turnCount > 0 ? (
          <Button
            size="small"
            variant="contained"
            color="inherit"
            startIcon={<ArrowUpwardIcon sx={{ fontSize: 16 }} />}
            onClick={scrollToTop}
            sx={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: 999,
              textTransform: "none",
              fontSize: 12,
              boxShadow: (t: Theme) => t.shadows[3],
              bgcolor: "background.paper",
              color: "text.primary",
              "&:hover": { bgcolor: "background.default" },
            }}
          >
            Jump to newest
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * Which turn keys appeared since the previous poll — so we only stream-in the
 * NEW arrivals, never the whole backlog. On first load every current key is
 * seeded as "seen" so the history doesn't all type out at once.
 */
function useNewTurnKeys(view: ChatItem[]): Set<string> {
  const seen = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  useEffect(() => {
    const keys = view
      .filter((c): c is Extract<ChatItem, { type: "turn" }> => c.type === "turn")
      .map((c) => c.turn.key);
    if (seen.current === null) {
      // Wait for the first poll that actually has turns, then seed every
      // current key as "seen" so the existing backlog doesn't all type out at
      // once — only genuinely new arrivals stream in.
      if (keys.length === 0) return;
      seen.current = new Set(keys);
      return;
    }
    const added = keys.filter((k) => !seen.current!.has(k));
    if (added.length) {
      added.forEach((k) => seen.current!.add(k));
      setFresh(new Set(added));
    }
  }, [view]);
  return fresh;
}

/**
 * Keep a scroll container pinned to the TOP as new content prepends — but only
 * while the operator is already at the top. If they scroll down to read
 * history, auto-follow pauses (`atTop` flips false so the caller can show a
 * "jump to newest" affordance); returning to the top resumes it.
 */
function useStickToTop(dep: number): {
  ref: React.RefObject<HTMLDivElement | null>;
  atTop: boolean;
  scrollToTop: () => void;
} {
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [atTop, setAtTop] = useState(true);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = (): void => {
      const isTop = el.scrollTop < 60;
      stick.current = isTop;
      setAtTop(isTop);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = 0;
  }, [dep]);

  const scrollToTop = (): void => {
    const el = ref.current;
    if (el) {
      el.scrollTop = 0;
      stick.current = true;
      setAtTop(true);
    }
  };

  return { ref, atTop, scrollToTop };
}
